import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import ReactGoogleMapLoader from 'react-google-maps-loader';
import ReactGooglePlacesSuggest from 'react-google-places-suggest';
import { isEmpty, cloneDeep } from 'lodash';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import InputText from '../Fields/InputText';
import Select from '../Fields/Select';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useStepPost from '../../hooks/useStepPost';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import {
  findStateLabelByIso2Code,
  findStateIso2CodeByLabel,
} from '../../util/helpers/countries';
import { getClientStates } from '../../store/actions/common/commonActions';
import {
  getEmploymentStatusList,
  getIncomeSourceList,
  getOccupationCategories,
} from '../../store/actions/employmentStatus/employmentStatusActions';
import {
  selectEmploymentStatusList,
  selectOccupationCategories,
  selectIncomeSourceList,
} from '../../store/selectors/employmentSelectors';
import {
  EMPTY_OPTIONS,
  getIncomeSourceOptions,
  getOccupationCategoryOptions,
} from '../../util/helpers/employmentStatusHelpers';
import {
  storeInSessionStorage,
  retrieveFromSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  BROKER_CODE,
  EMPLOYMENT_STATUS_OPTIONS,
  INCOME_SOURCE_OPTIONS,
  OCCUPATION_CATEGORIES,
  OCCUPATIONS,
  ACCOUNT_TYPE_GTM,
} from '../../constants/sessionStorage';
import {
  selectClientCountries,
  selectClientStates,
} from '../../store/selectors/commonSelectors';
import useEmploymentInfoDispatch from '../../hooks/useEmploymentStatusDispatch';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_VIEW, ACCOUNT_STEP_COMPLETE } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const SuggestItemWrapper = styled.div`
  padding: 15px;
`;

const EmploymentStatusForm = ({
  nextPath,
  // applicantType,
  onModalClose,
  eventType,
  setEventType,
  formId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { employmentInfoDispatch } = useEmploymentInfoDispatch();
  const countries = useSelector(selectClientCountries);
  const states = useSelector(selectClientStates);
  const { BrokerCode } = useSelector(selectSettings);
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const Application = useSelector(selectApplication);
  // const { ApplicationType } = Application;
  const employmentStatusList = useSelector(selectEmploymentStatusList);
  const { OccupationCategories, Occupations } = useSelector(
    selectOccupationCategories,
  );
  const IncomeSourceList = useSelector(selectIncomeSourceList);
  const ApplicantData = useApplicantData();
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [employmentStatusOptions, setEmploymentStatusOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [address, setAddress] = useState({});
  const [statesOptions, setStatesOptions] = useState([]);
  const [stateValue, setStateValue] = useState('');
  const [formDefaultValues, setFormDefaultValues] = useState({});
  const [occupationCategoryOptions, setOccupationCategoryOptions] = useState(
    [],
  );
  const [incomeSourceOptions, setIncomeSourceOptions] = useState([]);
  const [selectedOccupationCategory, setSelectedOccupationCategory] = useState(
    '',
  );
  const [hasError, setHasError] = useState(false);
  const [brokerCode, setBrokerCode] = useState('');
  useEffect(() => {
    if (BrokerCode) {
      setBrokerCode(BrokerCode);
    } else {
      const bCode = retrieveFromSessionStorage(BROKER_CODE);
      setBrokerCode(bCode);
    }
  }, [BrokerCode]);
  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  const isPersonAddressEmpty = (address) =>
    address && Object.values(address).every((value) => value === null);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Ownership Information',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      ...(isEmpty(ApplicantData?.Phone?.LocalNumber)
        ? {}
        : { accountPhone: ApplicantData?.Phone }),
      ...(isPersonAddressEmpty(ApplicantData?.PersonAddress)
        ? {}
        : { accountAddress: ApplicantData?.PersonAddress }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Ownership Information',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      ...(isEmpty(ApplicantData?.Phone?.LocalNumber)
        ? {}
        : { accountPhone: ApplicantData?.Phone }),
      ...(isPersonAddressEmpty(ApplicantData?.PersonAddress)
        ? {}
        : { accountAddress: ApplicantData?.PersonAddress }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onRequestSuccess = () => {
    handleGtmEvent();
    onSuccess();
  };

  useEffect(() => {
    if (Application && !isEmpty(Application)) {
      const employmentStatuses = retrieveFromSessionStorage(
        EMPLOYMENT_STATUS_OPTIONS,
      );
      const occupationCategories = retrieveFromSessionStorage(
        OCCUPATION_CATEGORIES,
      );
      const occupations = retrieveFromSessionStorage(OCCUPATIONS);
      const incomeSources = retrieveFromSessionStorage(INCOME_SOURCE_OPTIONS);
      if (
        !occupationCategories ||
        !occupations ||
        !incomeSources ||
        isEmpty(occupations) ||
        isEmpty(occupationCategories) ||
        isEmpty(incomeSources)
      ) {
        return;
      }
      const {
        IRAApplication: {
          CustomerDueDiligenceResponse: { IRAEmploymentInfoResponse },
        },
      } = Application;
      const existingEmploymentStatus =
        IRAEmploymentInfoResponse?.EmploymentStatus || null;
      const existingOccupationCategory =
        IRAEmploymentInfoResponse?.OccupationCategory ||
        IRAEmploymentInfoResponse?.PriorOccupationCategory;
      const existingOccupation =
        IRAEmploymentInfoResponse?.Occupation ||
        IRAEmploymentInfoResponse?.PriorOccupation;
      const existingIncomeSourceID =
        IRAEmploymentInfoResponse?.IncomeSourceTypeID;
      const existingEmploymentStatusEntry =
        employmentStatuses && !isEmpty(employmentStatuses)
          ? employmentStatuses.find((x) => x.value === existingEmploymentStatus)
          : null;
      const existingOccupationCategoryIndex = occupationCategories?.indexOf(
        existingOccupationCategory,
      );
      const existingOccupationIndex =
        occupations[`${existingOccupationCategory}`]?.indexOf(
          existingOccupation,
        ) ?? null;
      const existingStateCode = IRAEmploymentInfoResponse?.EmployerState;
      const existingStateLabel = findStateLabelByIso2Code(
        states,
        existingStateCode,
      );

      const defaultValues = {
        EmploymentStatus: existingEmploymentStatusEntry || null,
        OccupationCategory: existingOccupationCategory
          ? {
              label: existingOccupationCategory,
              value: existingOccupationCategoryIndex + 1,
            }
          : null,
        Occupation: existingOccupation
          ? {
              label: existingOccupation || '',
              value: existingOccupationIndex + 1 || '',
            }
          : null,
        SourceOfIncome: existingIncomeSourceID
          ? {
              label: incomeSources[existingIncomeSourceID - 1]?.label,
              value: existingIncomeSourceID,
            }
          : null,
        EmployerName: IRAEmploymentInfoResponse?.EmployerName,
        EmployerAddressLine: IRAEmploymentInfoResponse?.EmployerAddressLine,
        EmployerCity: IRAEmploymentInfoResponse?.EmployerCity,
        EmployerState: existingStateLabel
          ? {
              label: existingStateLabel,
              value: existingStateCode,
            }
          : null,
        EmployerZipCode: IRAEmploymentInfoResponse?.EmployerZipCode,
      };
      setFormValue('EmployerState', {
        value: existingStateCode,
        label: existingStateLabel,
      });
      if (existingOccupationCategory) {
        setSelectedOccupationCategory(existingOccupationCategory);
      }
      setFormDefaultValues(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application, states]);

  useEffect(() => {
    dispatch(getEmploymentStatusList());
    dispatch(getOccupationCategories());
    dispatch(getIncomeSourceList());
  }, [dispatch]);

  useEffect(() => {
    if (employmentStatusList && !isEmpty(employmentStatusList)) {
      const options = employmentStatusList.map((option) => ({
        label: option.Caption,
        value: option.EmploymentStatusID,
      }));
      storeInSessionStorage(EMPLOYMENT_STATUS_OPTIONS, options);
      setEmploymentStatusOptions(options);
    }

    if (OccupationCategories && !isEmpty(OccupationCategories)) {
      storeInSessionStorage(OCCUPATION_CATEGORIES, OccupationCategories);
      const occupationsOptions = getOccupationCategoryOptions(
        OccupationCategories,
      );
      setOccupationCategoryOptions(occupationsOptions);
    }

    if (Occupations && !isEmpty(Occupations)) {
      storeInSessionStorage(OCCUPATIONS, Occupations);
    }

    if (IncomeSourceList && !isEmpty(IncomeSourceList)) {
      const incomeSourceOptions = getIncomeSourceOptions(IncomeSourceList);
      storeInSessionStorage(INCOME_SOURCE_OPTIONS, incomeSourceOptions);
      setIncomeSourceOptions(incomeSourceOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employmentStatusList,
    OccupationCategories,
    Occupations,
    IncomeSourceList,
  ]);

  useEffect(() => {
    updateAddressForm(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: [
        'EmploymentStatus',
        'OccupationCategory',
        'Occupation',
        'EmployerName',
        'SourceOfIncome',
        'EmployerAddressLine',
        'EmployerState',
        'EmployerCity',
        'EmployerZipCode',
      ],
    }),
  });

  useEffect(() => {
    if (states && !isEmpty(states)) {
      const STATE_OPTIONS = [
        ...states.map((state) => ({
          value: state.Iso2Code,
          label: state.Caption,
          dataCy: 'menu-item-state',
        })),
      ];
      setStatesOptions(STATE_OPTIONS);
    }
  }, [countries, states]);

  const { handleSubmit, control, setValue, watch, reset, setError } = useForm({
    resolver: yupResolver(formValidationSchema),
    formDefaultValues,
  });

  useEffect(() => {
    if (Application && !isEmpty(Application)) {
      reset({ ...formDefaultValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application, reset]);

  const stateField = watch('EmployerState');
  const cityField = watch('EmployerCity');
  const employmentStatus = watch('EmploymentStatus');

  const setFormValue = (label, value) => {
    if (value) {
      setValue(label, value, { shouldValidate: true });
    }
  };

  const updateAddressForm = (address) => {
    if (address) {
      const {
        EmployerAddressLine,
        EmployerCity,
        EmployerZipCode,
        EmployerState,
      } = address;

      setFormValue('EmployerAddressLine', EmployerAddressLine);

      if (!EmployerCity && !cityField) {
        setValue('EmployerCity', '');
      } else {
        setFormValue('EmployerCity', EmployerCity, { shouldDirty: true });
      }
      if (!EmployerZipCode) {
        setValue('EmployerZipCode', '', { shouldDirty: true });
      } else {
        setFormValue('EmployerZipCode', EmployerZipCode);
      }

      if (!EmployerState?.value) {
        setValue('EmployerState', '', { shouldDirty: true });
      }

      if (EmployerState) {
        setFormValue('EmployerState', {
          value: EmployerState,
          label: findStateLabelByIso2Code(states, EmployerState),
        });
      }
    }
  };

  const clearAddressForm = () => {
    setStateValue('');
    setValue('EmployerName', '');
    setValue('EmployerState', '');
    setValue('EmployerAddressLine', '');
    setValue('EmployerCity', '');
    setValue('EmployerZipCode', '');
  };

  const resetExceptEmploymentStatus = (key) => {
    reset({
      EmploymentStatus: {
        value: key.value,
        label: key.label,
      },
      OccupationCategory: null,
      Occupation: null,
      SourceOfIncome: null,
      EmployerName: '',
      EmployerAddressLine: '',
      EmployerCity: '',
      EmployerState: null,
      EmployerZipCode: '',
    });
  };

  useEffect(() => {
    dispatch(getClientStates({ countryCode: 'USA' }));
  }, [dispatch]);

  useEffect(() => {
    if (stateField && stateField?.value) {
      setFormValue('EmployerState', {
        value: stateField?.value,
        label: findStateLabelByIso2Code(states, stateField?.value),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]);

  const processAddress = (addressDetails) => {
    const addressDetailTypeMap = {
      street_number: 'EmployerAddressLine',
      route: 'EmployerAddressLine',
      locality: 'EmployerCity',
      postal_town: 'EmployerCity',
      administrative_area_level_3: 'EmployerCity',
      administrative_area_level_1: 'EmployerState',
      postal_code: 'EmployerZipCode',
    };

    const addressMap = {
      EmployerAddressLine: {
        prop: 'short_name',
        values: [],
      },
      EmployerCity: {
        prop: 'long_name',
        values: [],
      },
      EmployerState: {
        prop: 'short_name',
        values: [],
      },
      EmployerZipCode: {
        prop: 'short_name',
        values: [],
      },
    };

    addressDetails.forEach((detail) => {
      detail.types.forEach((type) => {
        if (addressDetailTypeMap[type]) {
          const { prop, process, values } = addressMap[
            addressDetailTypeMap[type]
          ];
          values.push(process ? process(detail[prop]) : detail[prop]);
        }
      });
    });
    setAddress(
      Object.fromEntries(
        Object.entries(addressMap).map(([key, { values }]) => [
          key,
          values.join(' '),
        ]),
      ),
    );
  };

  const handleSelectSuggest = (geocodedPrediction) => {
    setSearch('');
    processAddress(geocodedPrediction.address_components);
  };

  useEffect(() => {
    const { EmployerState } = address;
    if (EmployerState) {
      setFormValue('EmployerState', {
        value: EmployerState,
        label: findStateLabelByIso2Code(states, EmployerState),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]);

  useEffect(() => {
    if (stateValue.length === 2) {
      setFormValue('EmployerState', {
        label: findStateLabelByIso2Code(states, stateValue),
        value: stateValue,
      });
    }

    if (stateValue.length > 2) {
      setFormValue('EmployerState', {
        label: stateValue,
        value: findStateIso2CodeByLabel(states, stateValue),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateValue, states]);

  const onSubmit = ({
    EmploymentStatus,
    OccupationCategory,
    Occupation,
    EmployerName,
    EmployerAddressLine,
    EmployerCity,
    EmployerState,
    // EmployerCountry,
    EmployerZipCode,
    SourceOfIncome,
  }) => {
    if (!EmploymentStatus || isEmpty(EmploymentStatus)) {
      return;
    }
    const isEmployed = EmploymentStatus?.value === 1;
    const payload = {
      ApplicationUid: Application?.ApplicationUid,
      data: {
        EmploymentStatus: EmploymentStatus?.value || null,
        EmployerName: isEmployed ? EmployerName : null,
        EmployerAddressLine: isEmployed ? EmployerAddressLine : null,
        EmployerCity: isEmployed ? EmployerCity : null,
        EmployerZipCode: isEmployed ? EmployerZipCode : null,
        EmployerState: isEmployed
          ? findStateIso2CodeByLabel(states, EmployerState.label)
          : null,
        OccupationCategory: isEmployed ? OccupationCategory?.label : null,
        Occupation: isEmployed ? Occupation?.label : null,
        PriorOccupationCategory: !isEmployed ? OccupationCategory?.label : null,
        PriorOccupation: !isEmployed ? Occupation?.label : null,
        IncomeSourceTypeID: !isEmployed ? SourceOfIncome?.value : null,
      },
    };

    let defaults = {};
    if (!isEmpty(formDefaultValues)) {
      defaults = cloneDeep(formDefaultValues);
      delete defaults.SourceOfIncome;
    }

    employmentInfoDispatch({
      data: payload.data,
      defaultValues: {
        ...defaults,
        EmploymentStatus: formDefaultValues?.EmploymentStatus?.value ?? null,
        IncomeSourceTypeID: formDefaultValues?.SourceOfIncome?.value ?? null,
        EmployerState: formDefaultValues?.EmployerState?.value ?? null,
        Occupation:
          EmploymentStatus === 1 ? formDefaultValues?.Occupation?.label : null,
        OccupationCategory:
          EmploymentStatus === 1
            ? formDefaultValues?.OccupationCategory?.label
            : null,
        PriorOccupation:
          EmploymentStatus !== 1 ? formDefaultValues?.Occupation?.label : null,
        PriorOccupationCategory:
          EmploymentStatus !== 1
            ? formDefaultValues?.OccupationCategory?.label
            : null,
      },
      onSuccess:
        eventType === 'Form'
          ? onRequestSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError: (error) => {
        if (eventType === 'Form') {
          setError(error.field, {
            type: error.type,
            message: error.message,
          });
        } else {
          setHasError(true);
          setSaveAndCloseModalOpen(true);
        }
      },
    });
  };

  const onChangeEmploymentStatus = (key) => {
    clearAddressForm();
    resetExceptEmploymentStatus(key);
  };

  const onChangeOccupationCategory = (category) => {
    setSelectedOccupationCategory(category.label);
    setValue('Occupation', null);
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormContainer mt={32}>
        <Controller
          control={control}
          render={({ field: { ref, onChange, ...field }, fieldState }) => (
            <Select
              label={t('employmentStatus')}
              options={
                employmentStatusOptions &&
                !isEmpty(employmentStatusOptions) &&
                employmentStatusOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))
              }
              {...field}
              {...fieldState}
              onChange={(...args) => {
                onChange(...args);
                onChangeEmploymentStatus(args[0]);
              }}
            />
          )}
          name="EmploymentStatus"
          defaultValue={formDefaultValues?.EmploymentStatus}
        />
        {employmentStatus && !isEmpty(employmentStatus) && (
          <Controller
            control={control}
            render={({ field: { ref, onChange, ...field }, fieldState }) => (
              <Select
                label={
                  employmentStatus.value !== 1
                    ? t('secureEmploymentStatus.titlePriorOccupationCategory')
                    : t('secureEmploymentStatus.titleOccupationCategory')
                }
                options={occupationCategoryOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                {...field}
                {...fieldState}
                onChange={(...args) => {
                  onChange(...args);
                  onChangeOccupationCategory(args[0]);
                }}
              />
            )}
            name="OccupationCategory"
            defaultValue={formDefaultValues?.OccupationCategory}
          />
        )}
        {employmentStatus && !isEmpty(employmentStatus) && (
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <Select
                label={
                  employmentStatus.value !== 1
                    ? t('secureEmploymentStatus.titlePriorOccupation')
                    : t('secureEmploymentStatus.titleOccupation')
                }
                options={
                  Occupations &&
                  !isEmpty(Occupations) &&
                  selectedOccupationCategory
                    ? getOccupationCategoryOptions(
                        Occupations[`${selectedOccupationCategory}`],
                      ).map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))
                    : EMPTY_OPTIONS
                }
                {...field}
                {...fieldState}
              />
            )}
            name="Occupation"
            defaultValue={formDefaultValues?.Occupation}
          />
        )}
        {employmentStatus &&
          !isEmpty(employmentStatus) &&
          employmentStatus.value > 1 && (
            <Controller
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <Select
                  label={t('secureEmploymentStatus.titleSourceOfIncome')}
                  options={incomeSourceOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  {...field}
                  {...fieldState}
                />
              )}
              name="SourceOfIncome"
              defaultValue={formDefaultValues?.SourceOfIncome}
            />
          )}
      </FormContainer>
      {employmentStatus &&
        !isEmpty(employmentStatus) &&
        employmentStatus.value === 1 && (
          <FormContainer mt={32}>
            <Controller
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <InputText
                  label={t('fields.labels.employerName')}
                  {...field}
                  {...fieldState}
                  autoFocus
                  data-cy="input-employer-name"
                />
              )}
              name="EmployerName"
              defaultValue={formDefaultValues?.EmployerName}
            />
            <ReactGoogleMapLoader
              params={{
                key: process.env.REACT_APP_MAPS_API_KEY,
                libraries: 'places',
              }}
              render={(googleMaps) =>
                googleMaps && (
                  <ReactGooglePlacesSuggest
                    googleMaps={googleMaps}
                    displayPoweredByGoogle={false}
                    autocompletionRequest={{
                      input: search,
                    }}
                    onSelectSuggest={handleSelectSuggest}
                    textNoResults={t('secureAddressEnter.notFound')}
                    customRender={(prediction) => (
                      <SuggestItemWrapper>
                        {prediction
                          ? prediction.description
                          : t('secureAddressEnter.notFound')}
                      </SuggestItemWrapper>
                    )}
                  >
                    <Controller
                      control={control}
                      render={({
                        field: { onChange, ...field },
                        fieldState,
                      }) => (
                        <InputText
                          {...field}
                          {...fieldState}
                          onChange={(e) => {
                            onChange(e);
                            setSearch(e.target.value);
                          }}
                          placeholder="Employer Address"
                          autocomplete="address-line1"
                          label={t('secureAddressEnter.employerStreet')}
                          data-cy="input-street-address"
                        />
                      )}
                      name="EmployerAddressLine"
                      defaultValue={formDefaultValues?.EmployerAddressLine}
                    />
                  </ReactGooglePlacesSuggest>
                )
              }
            />
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  {...field}
                  {...fieldState}
                  label={t('secureAddressEnter.city')}
                  data-cy="input-city"
                />
              )}
              name="EmployerCity"
              defaultValue={formDefaultValues?.EmployerCity}
            />
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  label={t('secureAddressEnter.employerState')}
                  options={statesOptions}
                  {...field}
                  {...fieldState}
                  dataCyButtonToggle="button-toggle-state"
                  minHeight={brokerCode === 'HAA' ? 46 : 42}
                />
              )}
              name="EmployerState"
              defaultValue={formDefaultValues?.EmployerState}
            />
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  {...field}
                  {...fieldState}
                  label={t('secureAddressEnter.zipCode')}
                  data-cy="input-postal-code"
                />
              )}
              name="EmployerZipCode"
              autocomplete="postal-code"
              defaultValue={formDefaultValues?.EmployerZipCode}
            />
          </FormContainer>
        )}
      <OnBoardingCta
        type="submit"
        onClick={() => {
          setEventType('Form');
        }}
        title={nextPath ? t('continue') : t('update')}
        buttonProps={{
          'data-cy': nextPath ? 'button-continue' : 'button-update',
        }}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

EmploymentStatusForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
  formId: PropTypes.string,
};

export default EmploymentStatusForm;
